import { gql, useQuery } from "@apollo/client";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useState, useCallback } from "react";
import { Platform, View, StyleSheet, TouchableOpacity } from "react-native";
import { ActivityIndicator, Badge, ProgressBar, Text, TouchableRipple } from "react-native-paper";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { Client as IntercomClient } from "../../hooks/useIntercom";

import { colors } from "../../../theme";
import { flatList, IconButton, ScrollView, Button, LinkButton } from "../../components";
import { useAppState } from "../../hooks";
import { EligibilityStatus, MainTabScreenProps } from "../types";
import {
  ACTIVITY_FRAGMENT,
  CURRENT_MEMBER_FRAGMENT,
  MY_ACTIVITY_FRAGMENT,
  Activity,
  MyActivity as Submission,
  CurrentMember,
  Plan,
} from "../fragments";
import { recentActivitiesList, findActivitiesList } from "./utils";
import { planStartYear } from "../utils";
import IneligibleModal from "./IneligibleModal";
import ChipPoint, { StatusText } from "../../components/ChipPoint";
import { Feather } from "@expo/vector-icons";
import QualifiedModal from "./QualifiedModal";
import config from "../../config";
import useIntercom from "../../hooks/useIntercom";
import { isEligible } from "../utils";
import ActivityImage from "../../components/ActivityImage";
import PostEnrollmentOnboarding from "./PostEnrollmentOnboarding";
import { useSettings } from "../../hooks/useSettings";
import moment from "moment";
import { Callout } from "./Callout";

export const QUERY = gql`
  query DeferredCompScreen {
    currentPlan: currentDeferredCompPlan {
      id
      minimumPointsThreshold
      confirmedPoints
      startsOn
      activities {
        ...ActivityFragment
      }
    }
    planTransition: deferredCompPlanTransition {
      previousPlan {
        id
        submissionWindowClosesOn
        minimumPointsThreshold
        confirmedPoints
        startsOn
      }

      nextPlan {
        startsOn
      }
    }
    currentMember {
      pendingSubmissions: deferredCompSubmissionsToReview {
        id
      }
      submissionsByExpiration: deferredCompSubmissions(
        limit: 3
        orderBy: EXPIRES_ON_ASC
        status: [PENDING, APPROVED, REJECTED, CANCELLED, EXPIRED]
      ) {
        ...MyActivityFragment
      }
      submissionsByUpdate: deferredCompSubmissions(limit: 3, orderBy: UPDATED_AT_DESC, status: [PENDING, APPROVED, REJECTED]) {
        ...MyActivityFragment
      }

      ...CurrentMemberFragment
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
  ${ACTIVITY_FRAGMENT}
  ${MY_ACTIVITY_FRAGMENT}
`;

const styles = StyleSheet.create({
  // @ts-ignore
  card: {
    ...Platform.select({ web: { cursor: "pointer" } }),
  },
});

const StyledActivityList = flatList<Activity>();

interface Member extends CurrentMember {
  pendingSubmissions: { id: string }[];
  submissionsByExpiration: [Submission];
  submissionsByUpdate: [Submission];
}

export interface PlanTransition {
  previousPlan?: Omit<Plan, "activities"> | null;
  nextPlan?: { startsOn: string } | null;
}

interface Query {
  currentPlan: Plan;
  planTransition: PlanTransition;
  currentMember: Member;
}

const openTransitionArticle = (intercom: IntercomClient) => () => {
  intercom.present(config.intercomContent.deferredCompPlanTransition);
};

const RecentActivityRow = ({ item, onPress }: { item: Submission; onPress: () => void }) => {
  const { status, expiresSoon, participationLevel, expirationDate } = item;
  const {
    points,
    activity: { name },
  } = participationLevel;

  return (
    <TouchableRipple onPress={onPress}>
      <View className="flex-row justify-between px-6 my-3">
        <View className="w-3/4">
          <Text className="text-regular-tight text-ink-darkest font-400">{name}</Text>
          <StatusText {...{ status, expiresSoon, expirationDate }} />
        </View>
        <View>
          <ChipPoint {...{ points, status, expiresSoon }} />
        </View>
      </View>
    </TouchableRipple>
  );
};

const ActivityCard = ({
  onTagPress,
  activity: { name, tags, maxPoints, imageUrl },
  onPress,
}: {
  onTagPress(tag: string): void;
  onPress(): void;
  activity: Activity;
}) => (
  <TouchableOpacity className="my-2 w-60 sm:w-56 bg-white" onPress={onPress} style={styles.card}>
    <ActivityImage imageUrl={imageUrl} imageClassName="rounded-lg w-full h-40" />
    <View className="flex-row flex-wrap pt-3 pr-3 pb-1">
      {tags.map(({ name }, index) => (
        <TouchableOpacity onPress={() => onTagPress(name)} key={name} style={{ marginRight: index < tags.length - 1 ? 8 : 0 }}>
          <Text className="text-primary-light">{name}</Text>
        </TouchableOpacity>
      ))}
    </View>

    <Text className="text-regular-tight font-500 mt-0.5">{name}</Text>

    <View className="flex-row items-center mt-3 mb-2 ml-2">
      <Text className="text-regular font-700">{maxPoints}</Text>

      <Text className="ml-1 text-small font-400">points max</Text>
    </View>
  </TouchableOpacity>
);

const UnenrolledContent = ({
  eligibilityStatus,
  navigateToEnroll,
}: {
  eligibilityStatus?: EligibilityStatus;
  navigateToEnroll(): void;
}) => {
  const [ineligibleModal, setIneligibleModal] = useState(false);

  const intercom = useIntercom();

  function onPress() {
    if (!isEligible(eligibilityStatus)) {
      setIneligibleModal(true);
    } else {
      navigateToEnroll();
    }
  }

  function onDismiss() {
    setIneligibleModal(false);
  }

  function onLearnMore() {
    intercom.present(config.intercomContent.deferredCompHomeLearnMore);
  }

  return (
    <>
      <IneligibleModal visible={ineligibleModal} {...{ onDismiss, eligibilityStatus }} />

      <Text className="mb-3 font-700 text-large-normal">Get started with deferred compensation</Text>

      <Text className="text-regular-normal font-400">
        Earn points and potential contributions to a deferred compensation plan simply by growing the game.
      </Text>

      <View className="sm:flex-row sm:items-end pt-8">
        <Button mode="contained" onPress={onPress} testID="enroll-button">
          Enroll
        </Button>

        <Button className="sm:ml-8 mt-3 sm:mt-0" onPress={onLearnMore}>
          Learn more
        </Button>
      </View>
    </>
  );
};

const EnrolledContent = ({
  navigateToActivities,
  navigateToMyActivityHistory,
  isSubmissionWindowClosed,
  plan,
}: {
  plan: Plan;
  isSubmissionWindowClosed: boolean;
  navigateToActivities(): void;
  navigateToMyActivityHistory(planId: string): void;
}) => {
  const [qualifiedModalVisible, setQualifiedModalVisible] = useState(false);
  const [postEnrollmentOnboardingVisible, setPostEnrollmentOnboardingVisible] = useState(false);
  const { planThresholdMet, deferredCompOnboarding, setDeferredCompOnboarding } = useSettings();
  const { confirmedPoints: points, minimumPointsThreshold } = plan;
  const progress = points / minimumPointsThreshold;
  const intercom = useIntercom();
  const isFocused = useIsFocused();

  useEffect(() => {
    if (points >= minimumPointsThreshold && !planThresholdMet) {
      setQualifiedModalVisible(true);
    }
  }, [points, planThresholdMet, minimumPointsThreshold]);

  useEffect(() => {
    if (!deferredCompOnboarding && isFocused) {
      setPostEnrollmentOnboardingVisible(true);
    }
  }, [deferredCompOnboarding, isFocused]);

  const onHelpResourcesPress = () => intercom.present();
  return (
    <>
      <QualifiedModal
        visible={qualifiedModalVisible}
        onDismiss={() => setQualifiedModalVisible(false)}
        minimumPointsThreshold={minimumPointsThreshold}
      />

      <PostEnrollmentOnboarding
        {...{
          visible: postEnrollmentOnboardingVisible,
          setPostEnrollmentOnboarded: setDeferredCompOnboarding,
          onDismiss: () => setPostEnrollmentOnboardingVisible(false),
        }}
      />

      <View className={`${isSubmissionWindowClosed ? "opacity-70" : ""}`}>
        <Text className="font-500 text-regular-normal text-ink-darker">Confirmed points</Text>
        <Text className="text-title-1 font-700 text-ink-darker" testID="confirmed-points">
          {points}
        </Text>

        {progress >= 1 ? (
          <View className="flex-row items-center">
            <View className="rounded-full bg-green-dark w-6 h-6 items-center justify-center">
              <Feather name="check" size={17} color={colors.sky.white} />
            </View>

            <Text className="text-small-tight font-400 text-ink-darker ml-2.5 mt-1">Now earning plan funds</Text>
          </View>
        ) : (
          <>
            <View className="flex-row items-center">
              <View className="flex-1">
                <ProgressBar progress={progress} className="rounded h-2 bg-sky" color={colors.secondary.lighter} />
              </View>

              <Text className="ml-2 text-small-tight font-700 text-secondary">{minimumPointsThreshold}</Text>
            </View>

            <Text className="text-tiny font-500 text-ink-darker mt-2">
              {minimumPointsThreshold} confirmed points required to earn plan funds
            </Text>
          </>
        )}
      </View>
      <View className="lg:flex-col sm:flex-row sm:items-center pt-12 sm:pt-10">
        {isSubmissionWindowClosed ? (
          <Button
            icon="arrow-right"
            iconPosition="right"
            testID="plan-activity-history"
            mode="contained"
            onPress={() => navigateToMyActivityHistory(plan.id)}
          >
            {planStartYear(plan)} Plan Activity History
          </Button>
        ) : (
          <Button icon="plus" size="large" iconPosition="right" mode="contained" onPress={navigateToActivities}>
            Add Points
          </Button>
        )}
        <View className="pt-7 sm:pt-0 sm:pl-8">
          <Button icon="help-circle" iconPosition="left" iconAlignment="adjacent" size="small" onPress={onHelpResourcesPress}>
            Help & Resources
          </Button>
        </View>
      </View>
    </>
  );
};

function Enrollment({
  currentMember,
  navigateToEnroll,
  navigateToActivities,
  navigateToMyActivityHistory,
  isSubmissionWindowClosed,
  plan,
}: {
  navigateToEnroll(): void;
  navigateToActivities(): void;
  navigateToMyActivityHistory(planId: string): void;
  isSubmissionWindowClosed: boolean;
  currentMember?: Member;
  plan: Plan;
}) {
  const { enrolled, eligibilityStatus } = currentMember || {};

  return enrolled ? (
    <EnrolledContent {...{ plan, isSubmissionWindowClosed, navigateToActivities, navigateToMyActivityHistory }} />
  ) : (
    <UnenrolledContent {...{ eligibilityStatus, navigateToEnroll }} />
  );
}

function TransitionCallout({
  selectedPlan,
  currentPlan,
  planTransition,
}: {
  selectedPlan: Plan;
  currentPlan: Plan;
  planTransition: PlanTransition;
}) {
  const intercom = useIntercom();

  if (planTransition.nextPlan?.startsOn) {
    const startDate = moment(planTransition.nextPlan.startsOn);

    return (
      <Callout level="info" onPress={openTransitionArticle(intercom)}>
        {startDate.format("YYYY")} plan year begins {startDate.format("MMMM Do")}
      </Callout>
    );
  } else if (selectedPlan.id === planTransition?.previousPlan?.id) {
    return selectedPlan.submissionWindowClosesOn ? (
      <Callout level="warn" onPress={openTransitionArticle(intercom)}>
        {planStartYear(selectedPlan)} submission window closing {moment(selectedPlan.submissionWindowClosesOn).format("M/DD")}
      </Callout>
    ) : (
      <Callout level="error" onPress={openTransitionArticle(intercom)}>
        {planStartYear(selectedPlan)} submission window closed
      </Callout>
    );
  } else {
    return (
      <Callout level="info" onPress={openTransitionArticle(intercom)}>
        {planStartYear(currentPlan)} plan year is live
      </Callout>
    );
  }
}

function PlanToggle({ plans, selected, select }: { selected: string; plans: Plan[]; select(plan: Plan): void }) {
  if (plans.length < 2) return null;
  return (
    <View className="items-start">
      <View className="flex-row rounded-lg mb-4 bg-sky-lighter">
        {plans.map((plan) => (
          <TouchableRipple
            key={plan.id}
            testID="plan-toggle"
            onPress={() => {
              select(plan);
            }}
            className={`rounded-lg py-2 px-4 border ${
              selected === plan.id ? "border-primary-light bg-sky-white" : "border-sky-lighter"
            }`}
          >
            <Text className={selected === plan.id ? "font-500 text-primary-light" : "font-400 text-ink-light"}>
              {planStartYear(plan)} Plan
            </Text>
          </TouchableRipple>
        ))}
      </View>
    </View>
  );
}

export default function DeferredCompScreen({ navigation }: MainTabScreenProps<"DeferredComp">) {
  const { isAppActive } = useAppState();

  const { loading, data, refetch } = useQuery<Query>(QUERY);

  const [selectedPlan, selectPlan] = useState<Plan | undefined>();

  useEffect(() => {
    if (isAppActive) refetch();
  }, [isAppActive]);

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [])
  );

  useEffect(() => {
    selectPlan(data?.currentPlan);
  }, [data?.currentPlan]);

  if (loading && !data) {
    return (
      <View className="flex-1 items-center justify-center mt-10">
        <ActivityIndicator testID="deferred-comp-screen-activity-indicator" />
      </View>
    );
  }

  const badgeCount = data?.currentMember.pendingSubmissions?.length || 0;
  const activities = data?.currentPlan?.activities || [];
  const submissionsByExpiration = data?.currentMember.submissionsByExpiration || [];
  const submissionsByUpdate = data?.currentMember.submissionsByUpdate || [];
  const recentActivities = recentActivitiesList({
    submissionsByExpiration,
    submissionsByUpdate,
  });
  const activitiesList = findActivitiesList(activities);

  const navigateToTag = (tag: string) => navigation.navigate("Activities", { tag });
  const navigateToActivities = () => navigation.navigate("Activities");
  const navigateToActivity =
    ({ code }: Activity) =>
    () =>
      navigation.navigate("Activity", { code });

  const navigateToActivityReviewList = () => navigation.navigate("ActivityReviewList");
  const navigateToMyActivity = (id: string, name: string) =>
    navigation.navigate("MyActivity", {
      id,
      ...(Platform.OS === "web" ? {} : { name }),
    });
  const navigateToMyActivityHistory = (plan?: string) => navigation.navigate("MyActivityHistory", plan ? { plan } : undefined);

  return (
    <View>
      <ScrollView testID="deferred-comp-screen" className="bg-white">
        <LinearGradient colors={["#ffffff", "#f8edd8"]} className="mt-1">
          <View className="p-6 max-w-3xl mx-auto w-full sm:pb-10 sm:mb-5">
            <View className="flex-row items-start justify-between w-full mt-2 mb-8">
              <Text className="flex-1 font-700 text-title-2 text-primary-dark">Deferred Compensation</Text>
              <View>
                <IconButton
                  icon="inbox"
                  size={32}
                  iconColor={colors.primary.DEFAULT}
                  className="mt-0 mr-0"
                  onPress={navigateToActivityReviewList}
                />
                <Badge
                  testID="badgeCount"
                  // @ts-ignore pointerEvents=none needs to be set to let IconButton touch events work
                  pointerEvents="none"
                  className="bg-red absolute"
                  visible={badgeCount > 0}
                >
                  {badgeCount}
                </Badge>
              </View>
            </View>

            {selectedPlan && data?.planTransition && (
              <>
                <PlanToggle
                  plans={[data?.currentPlan, data?.planTransition?.previousPlan].filter((plan) => !!plan) as Plan[]}
                  selected={selectedPlan.id}
                  select={selectPlan}
                />
                <TransitionCallout
                  selectedPlan={selectedPlan}
                  currentPlan={data?.currentPlan}
                  planTransition={data?.planTransition}
                />
              </>
            )}
            {selectedPlan && (
              <Enrollment
                navigateToEnroll={() => navigation.navigate("Enroll")}
                navigateToMyActivityHistory={navigateToMyActivityHistory}
                currentMember={data?.currentMember}
                isSubmissionWindowClosed={
                  selectedPlan.id === data?.planTransition?.previousPlan?.id && !selectedPlan.submissionWindowClosesOn
                }
                {...{
                  navigateToActivities,
                  plan: selectedPlan,
                }}
              />
            )}
          </View>
        </LinearGradient>

        <View className="max-w-3xl mx-auto w-full">
          {!!recentActivities.length && (
            <View className="mt-16">
              <View className="flex-row items-center justify-between px-6 pb-4">
                <Text className="text-title-5 text-ink-darkest font-700">Recent Activities</Text>
              </View>
              <>
                {recentActivities.map((item) => (
                  <View key={item.id}>
                    <RecentActivityRow
                      item={item}
                      onPress={() => navigateToMyActivity(item.id, item.participationLevel.activity.name)}
                    />
                    <View className="border-t border-sky-light sm:mx-6" />
                  </View>
                ))}
                <View className="p-6 sm:flex-row">
                  <Button
                    mode="outlined"
                    icon="arrow-right"
                    iconPosition="right"
                    onPress={() => navigateToMyActivityHistory()}
                    size={Platform.OS === "web" ? "small" : "large"}
                  >
                    My Activity History
                  </Button>
                </View>
              </>
            </View>
          )}

          <View className="my-10">
            <View className="flex-row items-center justify-between sm:justify-start sm:items-end pb-4 px-6">
              <Text className="text-title-5 text-ink-darkest font-700">Find Activities</Text>
              <View className="border-l border-ink h-5 mx-4 hidden sm:flex"></View>
              <LinkButton onPress={navigateToActivities}>See All</LinkButton>
            </View>

            <StyledActivityList
              horizontal
              contentContainerStyle="web:w-full sm:flex-row web:sm:flex-wrap px-6"
              showsHorizontalScrollIndicator={false}
              ItemSeparatorComponent={() => <View className="w-4 sm:w-2" />}
              data={activitiesList}
              renderItem={({ item }) => (
                <ActivityCard activity={item} onTagPress={navigateToTag} onPress={navigateToActivity(item)} />
              )}
            />

            {Platform.OS === "web" && (
              <View className="hidden sm:flex sm:flex-row sm:mt-3 px-6">
                <Button mode="outlined" size="small" onPress={navigateToActivities}>
                  See All Activities
                </Button>
              </View>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}
